import { z } from 'zod';
/**
 * Platform-level roles inside a customer organization. Product roles (for example the broker roles
 * sales, operations, claims, finance) belong to each product and are granted per product, not here.
 */
export const ORG_ROLES = ['org_owner', 'org_admin', 'billing_admin', 'member', 'help_desk'];
export const orgRoleSchema = z.enum(ORG_ROLES);
/** Roles of an external customer inside a provider organization's portal. */
export const PORTAL_ROLES = ['portal_admin', 'portal_user'];
export const portalRoleSchema = z.enum(PORTAL_ROLES);
/**
 * Trace staff roles. Separate from customer roles: being org_admin of any organization, including
 * Trace's own, never grants a staff role.
 */
export const STAFF_ROLES = [
    'platform_admin',
    'support_operator',
    'support_viewer',
    'billing_operator',
];
export const staffRoleSchema = z.enum(STAFF_ROLES);
/** How every surface names a staff role to people (Trace Admin, the hub, the staff invitation email). */
export const STAFF_ROLE_LABELS = {
    platform_admin: 'Platform admin',
    support_operator: 'Support operator',
    support_viewer: 'Support viewer',
    billing_operator: 'Billing operator',
};
export const PLATFORM_PERMISSIONS = [
    'org.read',
    'org.update',
    'members.read',
    'members.manage',
    'products.read',
    'modules.configure',
    'domains.read',
    'domains.manage',
    'portal.manage',
    'connectors.read',
    'connectors.manage',
    'billing.read',
    'billing.manage',
    'audit.read',
    'tickets.create',
    'tickets.read_own',
    'tickets.read_all',
    'tickets.manage',
    'tickets.escalate',
];
/** 0.8.0: one organization permission, as the organization administration API and me/access name it. */
export const platformPermissionSchema = z.enum(PLATFORM_PERMISSIONS);
/**
 * Versioned role-to-permission map (organization and staff roles). Changing it is a contract change: bump
 * ROLE_MAP_VERSION. 2 (contracts 0.9.0): support_viewer reads billing (staff.billing.read).
 */
// 3 (2026-10-13): help_desk org role + ticket permissions
export const ROLE_MAP_VERSION = 3;
const TICKET_BASE = ['tickets.create', 'tickets.read_own'];
const TICKET_DESK = [
    ...TICKET_BASE,
    'tickets.read_all',
    'tickets.manage',
    'tickets.escalate',
];
export const ORG_ROLE_PERMISSIONS = {
    org_owner: PLATFORM_PERMISSIONS,
    org_admin: [
        'org.read',
        'org.update',
        'members.read',
        'members.manage',
        'products.read',
        'modules.configure',
        'domains.read',
        'domains.manage',
        'portal.manage',
        'connectors.read',
        'connectors.manage',
        'billing.read',
        'audit.read',
        ...TICKET_DESK,
    ],
    billing_admin: ['org.read', 'products.read', 'billing.read', 'billing.manage', ...TICKET_BASE],
    member: ['org.read', 'products.read', ...TICKET_BASE],
    // Works the help desk only: no members, billing, modules or other administration.
    help_desk: ['org.read', 'products.read', ...TICKET_DESK],
};
export const STAFF_PERMISSIONS = [
    'staff.orgs.read',
    'staff.orgs.manage',
    'staff.members.manage',
    'staff.domains.manage',
    'staff.billing.read',
    'staff.billing.manage',
    'staff.jobs.read',
    'staff.support_access.request',
    'staff.audit.read',
    'staff.tickets.read',
    'staff.tickets.reply',
    'staff.tickets.manage',
    'staff.tickets.labels.manage',
];
export const STAFF_ROLE_PERMISSIONS = {
    platform_admin: STAFF_PERMISSIONS,
    support_operator: [
        'staff.orgs.read',
        'staff.members.manage',
        'staff.domains.manage',
        'staff.jobs.read',
        'staff.support_access.request',
        'staff.audit.read',
        'staff.tickets.read',
        'staff.tickets.reply',
        'staff.tickets.manage',
    ],
    // Read only everywhere, billing included (contracts 0.9.0); it changes nothing.
    support_viewer: [
        'staff.orgs.read',
        'staff.billing.read',
        'staff.jobs.read',
        'staff.audit.read',
        'staff.tickets.read',
        'staff.tickets.reply',
    ],
    billing_operator: [
        'staff.orgs.read',
        'staff.billing.read',
        'staff.billing.manage',
        'staff.audit.read',
        'staff.tickets.read',
        'staff.tickets.reply',
    ],
};
export function orgRolesGrant(roles, permission) {
    return roles.some((r) => ORG_ROLE_PERMISSIONS[r].includes(permission));
}
/**
 * 0.8.0: the permissions a role set holds, once each, in the order of PLATFORM_PERMISSIONS (so two role sets with
 * the same permissions give equal lists).
 */
export function orgRolePermissions(roles) {
    return PLATFORM_PERMISSIONS.filter((permission) => orgRolesGrant(roles, permission));
}
/**
 * 0.8.0: the permissions that make a person an administrator of their organization: every permission except
 * org.read and products.read, which every member holds. The organization administration API
 * (/v1/organizations/{orgId}/..., organization-admin.ts) serves administrators only: a role set without any of
 * these (a plain member) is refused on every route, and then each route needs its own permission.
 */
export const ORGANIZATION_ADMINISTRATION_PERMISSIONS = PLATFORM_PERMISSIONS.filter(
// 0.18.0: tickets.* never make an administrator: the help_desk role works tickets and nothing else.
(permission) => permission !== 'org.read' && permission !== 'products.read' && !permission.startsWith('tickets.'));
/** 0.8.0: true when the role set holds at least one administrative permission (ORGANIZATION_ADMINISTRATION_PERMISSIONS). */
export function orgRolesAdminister(roles) {
    return ORGANIZATION_ADMINISTRATION_PERMISSIONS.some((permission) => orgRolesGrant(roles, permission));
}
export function staffRoleGrants(role, permission) {
    return STAFF_ROLE_PERMISSIONS[role].includes(permission);
}
//# sourceMappingURL=roles.js.map